// A Service vessel's five dates, in the order the Service team works through them, each kept apart (an
// estimate is never the actual date) and each in its own column of the Service sheet:
//   Estimated Vessel Arrival Date   → "Estimated Vessel Arrival Date" (the app adds it the first time it's set)
//   Actual Vessel Arrival Date      → "Actual Vessel Arrival Date"    (likewise)
//   Work Starting Date              → "Start Date"
//   Estimated Work Completion Date  → "Expected delivery date"
//   Actual Work Completion Date     → "Delivery date"
// The page shows them by these names; the sheet keeps its own column titles. The works keep their own
// Start / Finish / Expected date; a vessel's Finish Date and Expected date (rarely set on a vessel) stay in
// its Fields panel.
import { findServiceColumn } from '@technohull/shared';

export type VesselDateKey = 'expectedArrival' | 'arrival' | 'start' | 'expectedCompletion' | 'completion';

/** the five, in order. */
export const VESSEL_DATE_KEYS: readonly VesselDateKey[] = ['expectedArrival', 'arrival', 'start', 'expectedCompletion', 'completion'];

/** what the page calls each. */
export const VESSEL_DATE_LABEL: Record<VesselDateKey, string> = {
  expectedArrival: 'Estimated Vessel Arrival Date',
  arrival: 'Actual Vessel Arrival Date',
  start: 'Work Starting Date',
  expectedCompletion: 'Estimated Work Completion Date',
  completion: 'Actual Work Completion Date',
};

interface DateCol { id: number; title: string; type?: string; primary?: boolean }

const PLANNED = /expect|estimat|plann|εκτιμ|αναμεν|αναμέν|προβλεπ/i;
const START = /start|begin|έναρξ|εναρξ/i;
const DELIVERY = /deliver|handover|παράδοσ|παραδοσ/i;
const COMPLETION = /complet|ολοκλ/i;
const isDate = (c: DateCol) => !c.primary && (c.type === 'DATE' || c.type === 'DATETIME');

/** the sheet's column for each of the vessel's five dates (undefined where it has none). The two arrival
 *  dates are found as the app's own columns (findServiceColumn); the rest by their titles, an estimate
 *  ("Expected …") never read as the actual date, and the works' own Expected date never one of them. */
export function serviceVesselDateColumns<C extends DateCol>(cols: readonly C[]): Record<VesselDateKey, C | undefined> {
  const dates = cols.filter(isDate);
  const expectedArrival = findServiceColumn('expectedArrival', dates);
  const arrival = findServiceColumn('arrival', dates);
  const worksExpected = findServiceColumn('expected', dates);
  const rest = dates.filter((c) => c !== expectedArrival && c !== arrival && c !== worksExpected);
  const planned = (c: C) => PLANNED.test(c.title);
  return {
    expectedArrival,
    arrival,
    start: rest.find((c) => START.test(c.title) && !planned(c)),
    expectedCompletion: rest.find((c) => planned(c) && (DELIVERY.test(c.title) || COMPLETION.test(c.title))),
    completion: rest.find((c) => !planned(c) && DELIVERY.test(c.title))
      ?? rest.find((c) => !planned(c) && /actual|πραγματικ/i.test(c.title) && COMPLETION.test(c.title)),
  };
}

/** the vessel's dates that the sheet has (or the app can add), in order, with the names the page shows. */
export function serviceVesselDateFields<C extends DateCol>(cols: readonly C[]): { key: VesselDateKey; label: string; col: C }[] {
  const found = serviceVesselDateColumns(cols);
  return VESSEL_DATE_KEYS.flatMap((key) => {
    const col = found[key];
    return col ? [{ key, label: VESSEL_DATE_LABEL[key], col }] : [];
  });
}
